// src/resources/dashboards/shared/index.tsx
//
// Kit compartilhado dos dashboards (Painel 360, Evolução do Produto, Voz do
// Cliente). Nasceu dentro do Painel360/ e subiu pra cá quando o segundo
// dashboard precisou das mesmas peças — manter duas cópias do skeleton/erro é
// exatamente o que fez o CustomerVoice reimplementar SkeletonCard 7 vezes.
//
// Painel360/shared.tsx virou um reexport deste arquivo, então os imports
// `from './shared'` das 7 seções continuam valendo.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, keyframes } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { motion } from 'framer-motion';
import { useDataProvider } from 'react-admin';
import { SectionPagination, SectionState } from './types';

export type { SectionPagination, SectionState } from './types';

// ─── Paleta / animação (mesma receita CustomerVoice/ProductEvolution) ─────────
export const COLORS = {
  brand: '#7869DF', brand2: '#667eea',
  success: '#22c55e', warning: '#f59e0b', danger: '#ef4444',
  blue: '#3b82f6', teal: '#14b8a6', purple: '#8b5cf6',
  ink: '#1e293b', muted: '#64748b', faint: '#94a3b8',
};

export const EASE_OUT: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

export const fadeSlideIn = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3, ease: EASE_OUT },
};

export const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const cardSx = {
  borderRadius: 4,
  bgcolor: 'rgba(255,255,255,0.9)',
  border: '1px solid rgba(120,105,223,0.10)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
  p: 2.25,
} as const;

// ─── Formatters pt-BR ──────────────────────────────────────────────────────────
export const fmtNum = (v: number | null | undefined, decimals = 0) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
export const fmtBrl = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
export const fmtPct = (v: number | null | undefined, decimals = 0) =>
  v === null || v === undefined ? '—' : `${(v * 100).toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;

// Uma seção que não pagina pede uma linha só (o payload inteiro vem em data[0]).
const SINGLE_ROW: SectionPagination = { page: 1, perPage: 1 };

// ─── Hook de fetch por seção com guard de corrida ──────────────────────────────
export function useDashboardSection<T>(
  resource: string,
  filter: Record<string, any>,
  enabled = true,
  // Só a carteira do Evolução do Produto usa isto: o payload dela é uma LISTA
  // paginada de verdade, e o dataProvider traduz page/perPage em skip/take
  // (store/providers/getAll.js). O default mantém o comportamento de sempre.
  pagination: SectionPagination = SINGLE_ROW,
  // Params de topo repassados ao dataProvider — hoje só `chainId`. O
  // store/providers/getAll.js faz `params.chainId || getChainId()`, ou seja: sem
  // isto toda seção iria para a rede do localStorage, e o detalhe da rede tem
  // que perguntar pela rede da URL (que pode não ser a selecionada no menu).
  extraParams?: Record<string, any>,
): SectionState<T> {
  const dataProvider = useDataProvider();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestRef = useRef(0);
  const filterKey = JSON.stringify(filter);
  // Chaves serializadas, não os objetos: o chamador monta o filtro inline a cada
  // render e a identidade nova re-dispararia o fetch em loop.
  const paginationKey = JSON.stringify(pagination);
  const extraKey = JSON.stringify(extraParams ?? {});

  const refetch = useCallback(async () => {
    if (!enabled) return;
    const id = ++requestRef.current;
    setLoading(true);
    setError(false);
    try {
      const result = await dataProvider.getList(resource, {
        pagination: JSON.parse(paginationKey),
        sort: { field: 'id', order: 'ASC' },
        filter: JSON.parse(filterKey),
        ...JSON.parse(extraKey),
      });
      if (id !== requestRef.current) return; // resposta antiga descartada
      setData((result.data?.[0] as unknown as T) ?? null);
      setLoading(false);
    } catch {
      if (id !== requestRef.current) return;
      setData(null);
      setError(true);
      setLoading(false);
    }
  }, [dataProvider, resource, filterKey, paginationKey, extraKey, enabled]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
}

// ─── Componentes base ──────────────────────────────────────────────────────────
export const SkeletonCard = ({ height = 220 }: { height?: number }) => (
  <Box sx={{
    ...cardSx, height,
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '200% 100%',
    animation: `${shimmer} 1.4s ease-in-out infinite`,
  }} />
);

export const ErrorCard = ({ onRetry, height = 220 }: { onRetry: () => void; height?: number }) => (
  <Box sx={{ ...cardSx, height, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
    <Typography variant="body2" color="text.secondary">Não foi possível carregar esta seção.</Typography>
    <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={onRetry}>Tentar de novo</Button>
  </Box>
);

export const EmptyHint = ({ text }: { text: string }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="body2" sx={{ color: COLORS.faint }}>{text}</Typography>
  </Box>
);

export const SectionHeader = ({ icon, gradient, title, subtitle, action }: {
  icon: React.ReactNode; gradient: string; title: string; subtitle: string;
  // Botão/link opcional no canto direito do cabeçalho (ex.: "Ver detalhes por
  // posto" da Penetração). Opcional e sem default pra não mexer nas outras
  // ~15 seções que já usam SectionHeader sem esse prop.
  action?: React.ReactNode;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, mb: 1.75 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: gradient, color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: 17, color: COLORS.ink }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>{subtitle}</Typography>
      </Box>
    </Box>
    {action}
  </Box>
);

export const Section = ({ index, children }: { index: number; children: React.ReactNode }) => (
  <motion.div {...fadeSlideIn} transition={{ ...fadeSlideIn.transition, delay: index * 0.07 }} style={{ marginTop: 28 }}>
    {children}
  </motion.div>
);

// ─── CountUp ───────────────────────────────────────────────────────────────────
export function useCountUp(target: number | null | undefined, duration = 1100): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === null || target === undefined) return;
    let frame: number;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / duration);
      setValue(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return value;
}

export const DeltaChip = ({ deltaPct }: { deltaPct: number | null }) => {
  if (deltaPct === null) return null;
  const up = deltaPct >= 0;
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, fontWeight: 700,
      borderRadius: 999, px: 1, py: 0.25, mt: 0.5,
      color: up ? COLORS.success : COLORS.danger,
      bgcolor: up ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
    }}>
      {up ? '▲' : '▼'} {fmtPct(Math.abs(deltaPct), 1)}
    </Box>
  );
};

/**
 * Atalhos dos painéis (Ver detalhes, Criar regra…) abrem em nova aba, para o painel continuar aberto
 * com os filtros onde estava. O admin usa hash router e guarda a rede no localStorage, então a aba
 * nova já abre na mesma rede.
 */
export const openInNewTab = (path: string) => {
  window.open(`#${path.startsWith('/') ? path : `/${path}`}`, '_blank', 'noopener');
};
